import { TTheme } from '@rbx/ui';

export const textClamp = (line = 1): Record<string, string | number> => {
  if (line === 1) {
    return {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    };
  }

  return {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    // line-clamp isn't yet supported by all browsers, so dual-write webkit-specific properties
    // https://developer.mozilla.org/en-US/docs/Web/CSS/Reference/Properties/line-clamp
    display: '-webkit-box',
    WebkitLineClamp: line,
    WebkitBoxOrient: 'vertical',
    lineClamp: line,
    boxOrient: 'vertical',
  };
};

export const wordBreakWrap: Record<string, string> = {
  // https://css-tricks.com/snippets/css/prevent-long-urls-from-breaking-out-of-container/
  // https://justmarkup.com/articles/2015-07-31-dealing-with-long-words-in-css/
  // https://developer.mozilla.org/en-US/docs/Web/CSS/word-break
  overflowWrap: 'break-word',
  wordWrap: 'break-word',
  msWordBreak: 'break-all',
  wordBreak: 'break-word', // deprecated, but is supported by all browsers
};

export const hideScrollbar: Record<string, unknown> = {
  // Firefox
  scrollbarWidth: 'none',
  // WebKit
  '&::-webkit-scrollbar': {
    display: 'none',
  },
};

export const engineRevampBaseTextStyle = {
  fontSize: '14px',
  fontStyle: 'normal',
  lineHeight: '140%',
};

export const engineRevampMonospaceTextStyle = {
  ...engineRevampBaseTextStyle,
  fontFamily: '"Builder Mono", monospace',
};

// Offset for sticky breadcrumb - used for scroll-margin-top on anchor targets
// 72px approximates the total height of ReferenceStickyBreadcrumb component:
// breadcrumb content (~32-40px) + padding (24px) + divider (1px) + buffer
export const stickyBreadcrumbScrollOffset = '72px';

export const getEngineRevampInlineCodeTagStyle = (theme: TTheme) => ({
  ...engineRevampMonospaceTextStyle,
  color: theme.palette.content.muted,
  display: 'inline-block',
  padding: '0px 4px',
  borderRadius: '4px',
  border: `1px solid ${theme.palette.components.divider}`,
  background: theme.palette.surface[100],
});

export const getEngineRevampInlineCodeLinkStyle = (theme: TTheme) => ({
  ...engineRevampMonospaceTextStyle,
  letterSpacing: '0px',
  textDecoration: 'underline',
  textDecorationStyle: 'solid',
  textDecorationSkipInk: 'auto',
  color: theme.palette.content.standard,
  display: 'inline-block',
  padding: '0px',
  backgroundColor: theme.palette.surface[0],
});

export const getEngineRevampHeadingStyle = (): React.CSSProperties => ({
  fontSize: '16px',
});

export const getEngineRevampParagraphStyle = (theme: TTheme): React.CSSProperties => ({
  color: theme.palette.content.muted,
  fontSize: '16px',
});

export const getEngineRevampLinkStyle = (theme: TTheme) => ({
  color: theme.palette.content.standard,
  textDecoration: 'underline',
  textDecorationStyle: 'solid',
  textDecorationSkipInk: 'auto',
});

export const getEngineRevampTableStyle = (theme: TTheme): React.CSSProperties => ({
  border: `1px solid ${theme.palette.components.divider}`,
  borderRadius: '4px',
  borderCollapse: 'separate',
  borderSpacing: 0,
  overflow: 'hidden',
});

export const getEngineRevampTableRowStyle = (theme: TTheme) => ({
  '& td, & th': {
    padding: '12px',
    borderBottom: `1px solid ${theme.palette.components.divider}`,
  },
  '&:last-child td': {
    borderBottom: 'none',
  },
});

export const getEngineRevampTableCellStyle = (theme: TTheme): React.CSSProperties => ({
  ...engineRevampBaseTextStyle,
  color: theme.palette.content.muted,
});

export const getEngineRevampTableHeaderCellStyle = (theme: TTheme): React.CSSProperties => ({
  ...engineRevampBaseTextStyle,
  fontWeight: 600,
  color: theme.palette.content.standard,
  backgroundColor: theme.palette.surface[0],
});

export default {
  textClamp,
  wordBreakWrap,
  hideScrollbar,
  engineRevampBaseTextStyle,
  engineRevampMonospaceTextStyle,
  stickyBreadcrumbScrollOffset,
  getEngineRevampInlineCodeTagStyle,
  getEngineRevampInlineCodeLinkStyle,
  getEngineRevampHeadingStyle,
  getEngineRevampParagraphStyle,
  getEngineRevampLinkStyle,
  getEngineRevampTableStyle,
  getEngineRevampTableRowStyle,
  getEngineRevampTableCellStyle,
  getEngineRevampTableHeaderCellStyle,
};
